import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar, { ASSETS } from '../components/Navbar';
import { 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Loader2,
  User,
  Briefcase,
  MapPin,
  Instagram,
  Heart,
  Sparkles,
  Target,
  MessageCircle,
  Crown,
  Star,
  Save,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Autosave debounce time in ms
const AUTOSAVE_DELAY = 2000;

const ApplicationPage = () => {
  const { user, isAuthenticated, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [currentBlock, setCurrentBlock] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState('');
  const [questionnaireId, setQuestionnaireId] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Block 0 - Basic Info
    basic_info: {
      name: '',
      age: '',
      city: '',
      occupation: '',
      instagram: '',
      gender: '',
      interested_in: ''
    },
    // Block 1 - Lifestyle
    lifestyle: {
      lifestyle_type: '',
      free_time_value: ''
    },
    // Block 2 - Intention
    intention: {
      looking_for: ''
    },
    // Block 3 - Personality
    personality: {
      personality_description: '',
      date_style: ''
    },
    // Block 4 - Connection
    connection: {
      attraction_factors: [],
      deal_breakers: ''
    },
    // Block 5 - Premium Filter
    premium_filter: {
      quality_importance: '',
      willing_to_invest: ''
    },
    // Block 6 - Intelligent Match
    intelligent_match: {
      ideal_person: '',
      incompatible_energy: ''
    },
    // Block 7 - Exclusivity
    exclusivity: {
      why_join: ''
    }
  });

  const blocks = [
    { id: 'basic_info', title: t('app.block1.title'), subtitle: t('app.block1.subtitle'), icon: User },
    { id: 'lifestyle', title: t('app.block2.title'), subtitle: t('app.block2.subtitle'), icon: Sparkles },
    { id: 'intention', title: t('app.block3.title'), subtitle: t('app.block3.subtitle'), icon: Target },
    { id: 'personality', title: t('app.block4.title'), subtitle: t('app.block4.subtitle'), icon: MessageCircle },
    { id: 'connection', title: t('app.block5.title'), subtitle: t('app.block5.subtitle'), icon: Heart },
    { id: 'premium_filter', title: t('app.block6.title'), subtitle: t('app.block6.subtitle'), icon: Crown },
    { id: 'intelligent_match', title: t('app.block7.title'), subtitle: t('app.block7.subtitle'), icon: Star },
    { id: 'exclusivity', title: t('app.block8.title'), subtitle: t('app.block8.subtitle'), icon: Sparkles }
  ];

  // Load existing questionnaire on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadQuestionnaire();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadQuestionnaire = async () => {
    try {
      const response = await axios.get(`${API}/questionnaire/me`, { withCredentials: true });
      const data = response.data;
      
      if (data.is_completed) {
        setIsCompleted(true);
        navigate('/dashboard');
        return;
      }
      
      setQuestionnaireId(data.questionnaire_id);
      setCurrentBlock(data.current_step || 0);
      setCompletedSteps(data.completed_steps || []);
      
      // Load saved data
      if (data.basic_info) setFormData(prev => ({ ...prev, basic_info: { ...prev.basic_info, ...data.basic_info } }));
      if (data.lifestyle) setFormData(prev => ({ ...prev, lifestyle: { ...prev.lifestyle, ...data.lifestyle } }));
      if (data.intention) setFormData(prev => ({ ...prev, intention: { ...prev.intention, ...data.intention } }));
      if (data.personality) setFormData(prev => ({ ...prev, personality: { ...prev.personality, ...data.personality } }));
      if (data.connection) setFormData(prev => ({ ...prev, connection: { ...prev.connection, ...data.connection } }));
      if (data.premium_filter) setFormData(prev => ({ ...prev, premium_filter: { ...prev.premium_filter, ...data.premium_filter } }));
      if (data.intelligent_match) setFormData(prev => ({ ...prev, intelligent_match: { ...prev.intelligent_match, ...data.intelligent_match } }));
      if (data.exclusivity) setFormData(prev => ({ ...prev, exclusivity: { ...prev.exclusivity, ...data.exclusivity } }));
      
      // Set name from user if not in form
      if (user?.name && !data.basic_info?.name) {
        setFormData(prev => ({
          ...prev,
          basic_info: { ...prev.basic_info, name: user.name }
        }));
      }
      
      setLastSaved(data.autosave_meta?.last_saved_at);
    } catch (err) {
      console.error('Error loading questionnaire:', err);
    } finally {
      setLoading(false);
    }
  };

  // Autosave function
  const saveStep = useCallback(async (step, data) => {
    if (!isAuthenticated) return;
    
    setSaving(true);
    try {
      const stepName = blocks[step].id;
      await axios.post(`${API}/questionnaire/save-step`, {
        step,
        step_name: stepName,
        data
      }, { withCredentials: true });
      
      setLastSaved(new Date().toISOString());
      
      // Update completed steps
      if (!completedSteps.includes(step)) {
        setCompletedSteps(prev => [...prev, step]);
      }
    } catch (err) {
      console.error('Error saving step:', err);
    } finally {
      setSaving(false);
    }
  }, [isAuthenticated, completedSteps, blocks]);

  // Debounced autosave
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentData = formData[blocks[currentBlock].id];
      if (Object.values(currentData).some(v => v && (Array.isArray(v) ? v.length > 0 : true))) {
        saveStep(currentBlock, currentData);
      }
    }, AUTOSAVE_DELAY);

    return () => clearTimeout(timer);
  }, [formData, currentBlock, saveStep, blocks]);

  const handleChange = (block, field, value) => {
    setFormData(prev => ({
      ...prev,
      [block]: { ...prev[block], [field]: value }
    }));
  };

  const handleMultiSelect = (block, field, value) => {
    setFormData(prev => {
      const current = prev[block][field] || [];
      if (current.includes(value)) {
        return {
          ...prev,
          [block]: { ...prev[block], [field]: current.filter(v => v !== value) }
        };
      }
      return {
        ...prev,
        [block]: { ...prev[block], [field]: [...current, value] }
      };
    });
  };

  const canProceed = () => {
    const data = formData[blocks[currentBlock].id];
    switch (currentBlock) {
      case 0: return data.name && data.age && data.city && data.occupation;
      case 1: return data.lifestyle_type && data.free_time_value;
      case 2: return data.looking_for;
      case 3: return data.personality_description && data.date_style;
      case 4: return data.attraction_factors?.length > 0 && data.deal_breakers;
      case 5: return data.quality_importance && data.willing_to_invest;
      case 6: return data.ideal_person && data.incompatible_energy;
      case 7: return data.why_join;
      default: return false;
    }
  };

  const goToNextBlock = async () => {
    // Save current step before moving
    const currentData = formData[blocks[currentBlock].id];
    await saveStep(currentBlock, currentData);
    
    setCurrentBlock(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  const goToPrevBlock = () => {
    setCurrentBlock(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    
    try {
      // Save final step
      const currentData = formData[blocks[currentBlock].id];
      await saveStep(currentBlock, currentData);
      
      // Submit questionnaire
      await axios.post(`${API}/questionnaire/submit`, {}, { withCredentials: true });
      
      navigate('/dashboard', { state: { justSubmitted: true } });
    } catch (err) {
      setError(err.response?.data?.detail || t('app.error.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  // Progress indicator component
  const ProgressIndicator = () => (
    <div className="mb-10">
      <div className="flex justify-between items-center mb-3">
        <span className="label-premium">{t('app.step')} {currentBlock + 1} {t('app.of')} {blocks.length}</span>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-[#8A8A8A] text-xs flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" />
              {t('app.saving')}
            </span>
          )}
          {lastSaved && !saving && (
            <span className="text-[#8A8A8A] text-xs flex items-center gap-1">
              <Save size={12} />
              {t('app.saved')}
            </span>
          )}
          <span className="text-[#8A8A8A] text-sm">
            {Math.round(((currentBlock + 1) / blocks.length) * 100)}%
          </span>
        </div>
      </div>
      
      {/* Step indicators */}
      <div className="flex gap-1 mb-2">
        {blocks.map((_, idx) => (
          <div
            key={idx}
            className={`h-1 flex-1 transition-all duration-500 ${
              idx < currentBlock 
                ? 'bg-[#8B1A1A]' 
                : idx === currentBlock 
                  ? 'bg-gradient-to-r from-[#8B1A1A] to-[#8B3A42]' 
                  : 'bg-[#8B1A1A]/20'
            }`}
          />
        ))}
      </div>
      
      {/* Progress bar */}
      <div className="h-[2px] bg-[#8B1A1A]/10 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#8B1A1A] to-[#8B3A42] transition-all duration-700"
          style={{ width: `${((currentBlock + 1) / blocks.length) * 100}%` }}
        />
      </div>
    </div>
  );

  // Option button component
  const OptionButton = ({ selected, onClick, children, testId }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-5 border transition-all text-left ${
        selected
          ? 'border-[#8B1A1A] bg-[#8B1A1A]/10 text-[#F5F0E6]'
          : 'border-[#8B1A1A]/10 text-[#8A8A8A] hover:border-[#8B1A1A]/30 hover:bg-[#8B1A1A]/5'
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );

  const renderBlockContent = () => {
    switch (currentBlock) {
      case 0: // Basic Info
        return (
          <div className="space-y-8">
            <div>
              <label className="label-premium block mb-3">{t('app.fields.name')}</label>
              <input
                type="text"
                value={formData.basic_info.name}
                onChange={(e) => handleChange('basic_info', 'name', e.target.value)}
                className="input-premium"
                placeholder={t('app.placeholders.name')}
                data-testid="app-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="label-premium block mb-3">{t('app.fields.age')}</label>
                <input
                  type="number"
                  value={formData.basic_info.age}
                  onChange={(e) => handleChange('basic_info', 'age', parseInt(e.target.value) || '')}
                  className="input-premium"
                  placeholder={t('app.placeholders.age')}
                  min={18}
                  max={100}
                  data-testid="app-age-input"
                />
              </div>
              <div>
                <label className="label-premium block mb-3">{t('app.fields.gender')}</label>
                <select
                  value={formData.basic_info.gender}
                  onChange={(e) => handleChange('basic_info', 'gender', e.target.value)}
                  className="input-premium"
                  data-testid="app-gender-select"
                >
                  <option value="">{t('app.select')}</option>
                  <option value="male">{t('app.gender.male')}</option>
                  <option value="female">{t('app.gender.female')}</option>
                  <option value="other">{t('app.gender.other')}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-premium block mb-3">{t('app.fields.city')}</label>
              <div className="relative">
                <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="text"
                  value={formData.basic_info.city}
                  onChange={(e) => handleChange('basic_info', 'city', e.target.value)}
                  className="input-premium pl-8"
                  placeholder={t('app.placeholders.city')}
                  data-testid="app-city-input"
                />
              </div>
            </div>
            <div>
              <label className="label-premium block mb-3">{t('app.fields.occupation')}</label>
              <div className="relative">
                <Briefcase className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="text"
                  value={formData.basic_info.occupation}
                  onChange={(e) => handleChange('basic_info', 'occupation', e.target.value)}
                  className="input-premium pl-8"
                  placeholder={t('app.placeholders.occupation')}
                  data-testid="app-occupation-input"
                />
              </div>
            </div>
            <div>
              <label className="label-premium block mb-3">{t('app.fields.instagram')}</label>
              <div className="relative">
                <Instagram className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="text"
                  value={formData.basic_info.instagram}
                  onChange={(e) => handleChange('basic_info', 'instagram', e.target.value)}
                  className="input-premium pl-8"
                  placeholder="@usuario"
                  data-testid="app-instagram-input"
                />
              </div>
            </div>
            <div>
              <label className="label-premium block mb-3">{t('app.fields.interested_in')}</label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'male', label: t('app.interested.men') },
                  { value: 'female', label: t('app.interested.women') },
                  { value: 'all', label: t('app.interested.all') }
                ].map(option => (
                  <OptionButton
                    key={option.value}
                    selected={formData.basic_info.interested_in === option.value}
                    onClick={() => handleChange('basic_info', 'interested_in', option.value)}
                    testId={`interested-${option.value}`}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </div>
            </div>
          </div>
        );

      case 1: // Lifestyle
        return (
          <div className="space-y-10">
            <div>
              <label className="label-premium block mb-5">{t('app.q.lifestyle')}</label>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: 'active', label: t('app.lifestyle.active') },
                  { value: 'social', label: t('app.lifestyle.social') },
                  { value: 'professional', label: t('app.lifestyle.professional') },
                  { value: 'balanced', label: t('app.lifestyle.balanced') }
                ].map(option => (
                  <OptionButton
                    key={option.value}
                    selected={formData.lifestyle.lifestyle_type === option.value}
                    onClick={() => handleChange('lifestyle', 'lifestyle_type', option.value)}
                    testId={`lifestyle-${option.value}`}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </div>
            </div>
            <div>
              <label className="label-premium block mb-5">{t('app.q.freetime')}</label>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: 'experiences', label: t('app.freetime.experiences') },
                  { value: 'growth', label: t('app.freetime.growth') },
                  { value: 'socializing', label: t('app.freetime.socializing') },
                  { value: 'tranquility', label: t('app.freetime.tranquility') }
                ].map(option => (
                  <OptionButton
                    key={option.value}
                    selected={formData.lifestyle.free_time_value === option.value}
                    onClick={() => handleChange('lifestyle', 'free_time_value', option.value)}
                    testId={`freetime-${option.value}`}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </div>
            </div>
          </div>
        );

      case 2: // Intention
        return (
          <div>
            <label className="label-premium block mb-5">{t('app.q.looking_for')}</label>
            <div className="space-y-4">
              {[
                { value: 'meet_people', label: t('app.intention.meet'), desc: t('app.intention.meet_desc') },
                { value: 'emotional_connection', label: t('app.intention.emotional'), desc: t('app.intention.emotional_desc') },
                { value: 'serious_relationship', label: t('app.intention.serious'), desc: t('app.intention.serious_desc') },
                { value: 'open', label: t('app.intention.open'), desc: t('app.intention.open_desc') }
              ].map(option => (
                <OptionButton
                  key={option.value}
                  selected={formData.intention.looking_for === option.value}
                  onClick={() => handleChange('intention', 'looking_for', option.value)}
                  testId={`looking-${option.value}`}
                >
                  <div className="text-[#F5F0E6] font-medium">{option.label}</div>
                  <div className="text-[#8A8A8A] text-sm mt-1">{option.desc}</div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 3: // Personality
        return (
          <div className="space-y-10">
            <div>
              <label className="label-premium block mb-3">{t('app.q.personality')}</label>
              <textarea
                value={formData.personality.personality_description}
                onChange={(e) => handleChange('personality', 'personality_description', e.target.value)}
                className="input-premium h-32 resize-none w-full bg-transparent"
                placeholder={t('app.placeholders.personality')}
                data-testid="personality-description"
              />
            </div>
            <div>
              <label className="label-premium block mb-5">{t('app.q.date_style')}</label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'reserved', label: t('app.datestyle.reserved') },
                  { value: 'balanced', label: t('app.datestyle.balanced') },
                  { value: 'expressive', label: t('app.datestyle.expressive') }
                ].map(option => (
                  <OptionButton
                    key={option.value}
                    selected={formData.personality.date_style === option.value}
                    onClick={() => handleChange('personality', 'date_style', option.value)}
                    testId={`datestyle-${option.value}`}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </div>
            </div>
          </div>
        );

      case 4: // Connection
        return (
          <div className="space-y-10">
            <div>
              <label className="label-premium block mb-5">{t('app.q.attraction')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { value: 'intelligence', label: t('app.attraction.intelligence') },
                  { value: 'energy', label: t('app.attraction.energy') },
                  { value: 'lifestyle', label: t('app.attraction.lifestyle') },
                  { value: 'humor', label: t('app.attraction.humor') },
                  { value: 'ambition', label: t('app.attraction.ambition') },
                  { value: 'kindness', label: t('app.attraction.kindness') }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleMultiSelect('connection', 'attraction_factors', option.value)}
                    className={`p-4 border transition-all flex items-center gap-2 ${
                      formData.connection.attraction_factors?.includes(option.value)
                        ? 'border-[#8B1A1A] bg-[#8B1A1A]/10 text-[#F5F0E6]'
                        : 'border-[#8B1A1A]/10 text-[#8A8A8A] hover:border-[#8B1A1A]/30'
                    }`}
                    data-testid={`attraction-${option.value}`}
                  >
                    {formData.connection.attraction_factors?.includes(option.value) && (
                      <Check size={16} className="text-[#8B1A1A]" />
                    )}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-premium block mb-3">{t('app.q.dealbreakers')}</label>
              <textarea
                value={formData.connection.deal_breakers}
                onChange={(e) => handleChange('connection', 'deal_breakers', e.target.value)}
                className="input-premium h-24 resize-none w-full bg-transparent"
                placeholder={t('app.placeholders.dealbreakers')}
                data-testid="deal-breakers"
              />
            </div>
          </div>
        );

      case 5: // Premium Filter
        return (
          <div className="space-y-10">
            <div>
              <label className="label-premium block mb-5">{t('app.q.quality')}</label>
              <div className="space-y-4">
                {[
                  { value: 'very_important', label: t('app.quality.very'), desc: t('app.quality.very_desc') },
                  { value: 'important', label: t('app.quality.important'), desc: t('app.quality.important_desc') },
                  { value: 'neutral', label: t('app.quality.neutral'), desc: t('app.quality.neutral_desc') }
                ].map(option => (
                  <OptionButton
                    key={option.value}
                    selected={formData.premium_filter.quality_importance === option.value}
                    onClick={() => handleChange('premium_filter', 'quality_importance', option.value)}
                    testId={`quality-${option.value}`}
                  >
                    <div className="text-[#F5F0E6] font-medium">{option.label}</div>
                    <div className="text-[#8A8A8A] text-sm mt-1">{option.desc}</div>
                  </OptionButton>
                ))}
              </div>
            </div>
            <div>
              <label className="label-premium block mb-5">{t('app.q.invest')}</label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'yes', label: t('app.invest.yes') },
                  { value: 'depends', label: t('app.invest.depends') },
                  { value: 'no', label: t('app.invest.no') }
                ].map(option => (
                  <OptionButton
                    key={option.value}
                    selected={formData.premium_filter.willing_to_invest === option.value}
                    onClick={() => handleChange('premium_filter', 'willing_to_invest', option.value)}
                    testId={`invest-${option.value}`}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </div>
            </div>
          </div>
        );

      case 6: // Intelligent Match
        return (
          <div className="space-y-10">
            <div>
              <label className="label-premium block mb-3">{t('app.q.ideal')}</label>
              <textarea
                value={formData.intelligent_match.ideal_person}
                onChange={(e) => handleChange('intelligent_match', 'ideal_person', e.target.value)}
                className="input-premium h-32 resize-none w-full bg-transparent"
                placeholder={t('app.placeholders.ideal')}
                data-testid="ideal-person"
              />
            </div>
            <div>
              <label className="label-premium block mb-3">{t('app.q.incompatible')}</label>
              <textarea
                value={formData.intelligent_match.incompatible_energy}
                onChange={(e) => handleChange('intelligent_match', 'incompatible_energy', e.target.value)}
                className="input-premium h-24 resize-none w-full bg-transparent"
                placeholder={t('app.placeholders.incompatible')}
                data-testid="incompatible-energy"
              />
            </div>
          </div>
        );

      case 7: // Exclusivity
        return (
          <div>
            <label className="label-premium block mb-3">{t('app.q.why_join')}</label>
            <textarea
              value={formData.exclusivity.why_join}
              onChange={(e) => handleChange('exclusivity', 'why_join', e.target.value)}
              className="input-premium h-40 resize-none w-full bg-transparent"
              placeholder={t('app.placeholders.why_join')}
              data-testid="why-join"
            />
            
            {/* Summary before submit */}
            <div className="mt-10 p-6 bg-[#8B1A1A]/5 border border-[#8B1A1A]/20">
              <h3 className="text-[#F5F0E6] font-serif text-lg mb-4">{t('app.summary.title')}</h3>
              <div className="space-y-2 text-sm text-[#8A8A8A]">
                <p><span className="text-[#8B1A1A]">{t('app.summary.name')}:</span> {formData.basic_info.name}</p>
                <p><span className="text-[#8B1A1A]">{t('app.summary.city')}:</span> {formData.basic_info.city}</p>
                <p><span className="text-[#8B1A1A]">{t('app.summary.intention')}:</span> {formData.intention.looking_for}</p>
                <p><span className="text-[#8B1A1A]">{t('app.summary.lifestyle')}:</span> {formData.lifestyle.lifestyle_type}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <img src={ASSETS.logo} alt="Loading..." className="w-20 h-20 logo-glow animate-pulse object-contain" />
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="pt-40 pb-20 px-6 max-w-2xl mx-auto text-center">
          <img 
            src={ASSETS.logo}
            alt="Soul Mate Dates"
            className="w-24 h-24 mx-auto mb-10 logo-glow object-contain"
          />
          <h1 className="text-4xl font-serif text-[#F5F0E6] mb-6">
            {t('app.login_required')}
          </h1>
          <p className="text-[#8A8A8A] mb-10">
            {t('app.login_message')}
          </p>
          <button
            onClick={loginWithGoogle}
            className="btn-gold inline-flex items-center gap-2"
            data-testid="login-to-apply"
          >
            {t('app.login_button')}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <ProgressIndicator />

          {/* Block Header */}
          <div className="mb-10">
            <div className="flex items-center gap-4 mb-4">
              {React.createElement(blocks[currentBlock].icon, {
                className: "text-[#8B1A1A]",
                size: 28
              })}
              <h1 className="text-3xl sm:text-4xl font-serif text-[#F5F0E6]">
                {blocks[currentBlock].title}
              </h1>
            </div>
            <p className="text-[#8A8A8A]">{blocks[currentBlock].subtitle}</p>
          </div>

          {/* Block Content */}
          <div className="mb-14">
            {renderBlockContent()}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-8 p-4 bg-red-900/20 border border-red-900/50 text-red-400 text-sm flex items-center gap-2">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={goToPrevBlock}
              disabled={currentBlock === 0}
              className={`flex items-center gap-2 px-6 py-3 transition-colors ${
                currentBlock === 0
                  ? 'text-[#8A8A8A]/30 cursor-not-allowed'
                  : 'text-[#8A8A8A] hover:text-[#F5F0E6]'
              }`}
              data-testid="prev-block"
            >
              <ArrowLeft size={18} />
              {t('app.nav.prev')}
            </button>

            {currentBlock < blocks.length - 1 ? (
              <button
                type="button"
                onClick={goToNextBlock}
                disabled={!canProceed()}
                className={`btn-gold flex items-center gap-2 ${
                  !canProceed() ? 'opacity-40 cursor-not-allowed' : ''
                }`}
                data-testid="next-block"
              >
                {t('app.nav.next')}
                <ArrowRight size={18} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canProceed() || submitting}
                className={`btn-gold flex items-center gap-2 ${
                  !canProceed() || submitting ? 'opacity-40 cursor-not-allowed' : ''
                }`}
                data-testid="submit-application"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    {t('app.nav.submit')}
                    <Check size={18} />
                  </>
                )}
              </button>
            )}
          </div>
          
          {/* Step navigation dots */}
          <div className="flex justify-center gap-2 mt-10">
            {blocks.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  if (completedSteps.includes(idx) || idx <= currentBlock) {
                    setCurrentBlock(idx);
                  }
                }}
                disabled={!completedSteps.includes(idx) && idx > currentBlock}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentBlock 
                    ? 'bg-[#8B1A1A] w-6' 
                    : completedSteps.includes(idx)
                      ? 'bg-[#8B1A1A]/60 hover:bg-[#8B1A1A]'
                      : 'bg-[#8B1A1A]/20'
                }`}
                data-testid={`step-dot-${idx}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;
